import api, { ValidateAxiosError } from '@/modules/api.service';
import { CreateLotacaoDto, ReadDefensoriaDto, ReadLotacaoDto, ReadSubstituicaoDto, UpdateLotacaoDto } from '@/api.types';
import { Endpoints } from '@/shared/constants/endpoints';
import { SearchDtoLotacoes } from '@/shared/types/search.dto';
import { isAxiosError } from 'axios';

export class LotacaoService {
  async Create(obj: CreateLotacaoDto, _userid?: number): Promise<number | string> {
    try {
      return (await api.post(Endpoints.LOTACAO, obj)).data;
    } catch (error) {
      // Se for um erro de conflitos (400 com estrutura de conflitos), retorna o erro original
      if (isAxiosError(error) && error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData && errorData.conflitos && Array.isArray(errorData.conflitos)) {
          throw error; // Re-lança o erro para ser tratado no componente
        }
      }
      return ValidateAxiosError(error, 'Falha ao cadastrar o Lotação.');
    }
  }

  async Read(id: number | string): Promise<ReadLotacaoDto> {
    try {
      return (await api.get(Endpoints.LOTACAO + '/' + id)).data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao buscar o Lotação.' + id);
    }
  }

  async Update(obj: UpdateLotacaoDto, id: number | string, _userid?: number): Promise<boolean> {
    try {
      return (await api.put(Endpoints.LOTACAO + '/' + id, obj)).data;
    } catch (error) {
      // Se for um erro de conflitos (400 com estrutura de conflitos), retorna o erro original
      if (isAxiosError(error) && error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData && errorData.conflitos && Array.isArray(errorData.conflitos)) {
          throw error; // Re-lança o erro para ser tratado no componente
        }
      }
      return ValidateAxiosError(error, 'Falha ao atualizar o Lotação.');
    }
  }

  async Delete(id: number, _userid?: number): Promise<boolean> {
    try {
      await api.delete(Endpoints.LOTACAO + '/' + id);
      return true;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao remover Lotação.');
    }
  }

  async Enumerate(conditions: SearchDtoLotacoes): Promise<[ReadLotacaoDto[], number]> {
    try {
      const res = await api
        .get(Endpoints.LOTACAO, { params: conditions })
        .then((response) => {
          return response.data;
        });
      return res;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao listar Lotações.');
    }
  }

  /**
   * Lista de lotações já com as substituições selecionadas.
   * @param id Identificação da lotação.
   * @param substituicoes Substituições já efetuadas.
   * @param lotacoes Defensorias em que está lotado.
   * @param titularidades Titularidades.
   * @returns
   */
  MergeLotacaoSubstituicao(id: number, substituicoes: ReadSubstituicaoDto[], lotacoes: ReadLotacaoDto[], titularidades: ReadDefensoriaDto[]) {
    //
    const listaDefensorias = lotacoes.map(
      (
        lotacao, //
      ) => {
        const subs = substituicoes?.find(
          (a) => a.defensoria?.id == lotacao.defensoria?.id,
        );
        let substituto = {
          id: -1,
          nome: '',
          ativo: true
        };
        let anotacao = '';

        if (subs) {
          // @ts-expect-error err
          substituto = subs.substituto!;
          anotacao = subs.anotacao!;
        } else if (id <= 0) {
          // Se não foi definido o defensor substituto e não é uma edição, usa o titular da defensoria substituta.
          const tit = titularidades.find((a) => a.id == lotacao.defensoria?.id);

          if (
            tit?.defensoriasubstituta1 &&
            tit.defensoriasubstituta1.lotacoes &&
            tit.defensoriasubstituta1.lotacoes.length > 0
          ) {
            const defensor = tit.defensoriasubstituta1.lotacoes[0].defensor;
            if (defensor) {
              substituto = {
                id: defensor.id,
                nome: defensor.pessoa?.nome || '',
                ativo: defensor.ativo ?? true
              };
            }
          } else if (
            tit?.defensoriasubstituta2 &&
            tit.defensoriasubstituta2.lotacoes &&
            tit.defensoriasubstituta2.lotacoes.length > 0
          ) {
            const defensor = tit.defensoriasubstituta2.lotacoes[0].defensor;
            if (defensor) {
              substituto = {
                id: defensor.id,
                nome: defensor.pessoa?.nome || '',
                ativo: defensor.ativo ?? true
              };
            }
          }
        }
        return {
          defensoria: lotacao.defensoria,
          substituto: substituto,
          anotacao: anotacao,
        } as ReadSubstituicaoDto;
      },
    );

    // Defensorias que saíram da lotação, mas ainda possuem substituição cadastrada.
    const idsLotacoes = lotacoes
      .map((item) => {
        return item.defensoria?.id;
      })
      .filter((value, index, array) => {
        return array.indexOf(value) === index;
      });

    const lista = substituicoes?.filter((item) => !idsLotacoes.includes(item.defensoria?.id))
      .filter((value, index, array) => {
        return array.indexOf(value) === index;
      });

    if (lista) {
      for (let i = 0; i < lista.length; i++) {
        const elemento = lista[i];
        if (
          listaDefensorias.findIndex(
            (item) => item.defensoria?.id == elemento.defensoria?.id,
          ) < 0
        )
          listaDefensorias.push({
            defensoria: elemento.defensoria,
            substituto: elemento.substituto,
            anotacao: elemento.anotacao,
          } as ReadSubstituicaoDto);
      }
    }

    return listaDefensorias;
  }

  /**
   * Valida o modelo para garantir que os campos foram preenchidos corretamente.
   */
  public Validate(obj: UpdateLotacaoDto) {
    // Valida mais de uma pessoa no mesmo registro.
    const { defensor, estagio } = obj;
    const selectedCount = [defensor, estagio].filter(Boolean).length;

    if (selectedCount > 1)
      throw new Error(
        'Deve ser especificado apenas um defensor, colaborador ou estágio no mesmo registro.',
      );
    else if (selectedCount <= 0)
      throw new Error(
        'Informe um defensor, colaborador ou estagiário.',
      );

    // Valida mais de um local.
    if (obj.defensoria && obj.nucleo)
      throw new Error(
        'Não é possível lotar em defensoria e núcleo no mesmo registro.',
      );

    if (!obj.defensoria && !obj.nucleo && !obj.setor)
      throw new Error('Informe uma defensoria, núcleo ou setor.');

    // Valida se houve troca de campos, antes era defensoria e passou a ser núcleo, para assegurar que não exista ambos.
    // Se defensoria existe, zera o núcleo, senão, se núcleo existe, zera a defensoria
    if (obj.defensoria != null) {
      // @ts-expect-error TS2322
      obj.nucleo = null;
    } else if (obj.nucleo != null) {
      // @ts-expect-error TS2322
      obj.defensoria = null;
    }

    // Exclusividade entre defensor, colaborador e estágio
    if (obj.defensor != null) {
      // @ts-expect-error TS2322
      obj.estagio = null;
    } else if (obj.responsavel != null) {
      // @ts-expect-error TS2322
      obj.defensor = null;
      // @ts-expect-error TS2322
      obj.estagio = null;
    } else if (obj.estagio != null) {
      // @ts-expect-error TS2322
      obj.defensor = null;
    }


    // Valida datas.
    if (obj.data_final && obj.data_final < obj.data_inicial!) {
      throw new Error(
        'A data final não pode ser menor que a inicial.',
      );
    }
  }
}